/**
 * Azure AD Authentication Service
 * Handles Microsoft Azure Active Directory authentication
 */
import {
    PublicClientApplication,
    AccountInfo,
    AuthenticationResult,
    InteractionRequiredAuthError,
} from '@azure/msal-browser';
import { authConfig } from '@core/config/auth.config';

class AzureADService {
    private msalInstance: PublicClientApplication | null = null;
    private lastAuthResult: AuthenticationResult | null = null;

    /**
     * Initialize MSAL instance
     */
    async initialize(): Promise<void> {
        if (!authConfig.azureAd) {
            throw new Error('Azure AD configuration not found');
        }

        this.msalInstance = new PublicClientApplication({
            auth: {
                clientId: authConfig.azureAd.clientId,
                authority: authConfig.azureAd.authority,
                redirectUri: authConfig.azureAd.redirectUri,
            },
            cache: {
                cacheLocation: 'sessionStorage',
                storeAuthStateInCookie: false,
            },
        });

        await this.msalInstance.initialize();
        await this.handleRedirectPromise();
    }

    /**
     * Handle redirect after login
     * Returns the authentication result if user just returned from redirect
     */
    async handleRedirectPromise(): Promise<AuthenticationResult | null> {
        if (!this.msalInstance) return null;

        try {
            const response = await this.msalInstance.handleRedirectPromise();
            if (response) {
                this.msalInstance.setActiveAccount(response.account);
                this.lastAuthResult = response; // Store for ID token access
                console.log('✅ Redirect authentication successful');
                return response;
            }
            return null;
        } catch (error) {
            console.error('Error handling redirect:', error);
            throw error;
        }
    }

    /**
     * Login with redirect (primary method to avoid popup issues)
     */
    async loginWithPopup(): Promise<AuthenticationResult> {
        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        // Use redirect instead of popup to avoid nested popup issues
        await this.msalInstance.loginRedirect({
            scopes: authConfig.azureAd?.scopes || ['User.Read'],
        });

        // This will never be reached as redirect navigates away
        // Return a promise that won't resolve (redirect happens first)
        return new Promise(() => {});
    }

    /**
     * Login with redirect
     */
    async loginWithRedirect(): Promise<void> {
        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        await this.msalInstance.loginRedirect({
            scopes: authConfig.azureAd?.scopes || ['User.Read'],
        });
    }

    /**
     * Get access token silently
     */
    async getAccessToken(): Promise<string> {
        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        const account = this.getAccount();
        if (!account) {
            throw new Error('No active account');
        }

        try {
            const response = await this.msalInstance.acquireTokenSilent({
                scopes: authConfig.azureAd?.scopes || ['User.Read'],
                account,
            });
            this.lastAuthResult = response; // Update stored result
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Use redirect instead of popup to avoid nested popup issues
                await this.msalInstance.acquireTokenRedirect({
                    scopes: authConfig.azureAd?.scopes || ['User.Read'],
                    account,
                });
                // This will never be reached as redirect navigates away
                return new Promise(() => {});
            }
            throw error;
        }
    }

    /**
     * Get ID token from last authentication result
     * ID token is required for SSO endpoint
     */
    getIdToken(): string | null {
        return this.lastAuthResult?.idToken || null;
    }

    /**
     * Get ID token, refreshing if necessary
     */
    async getIdTokenAsync(): Promise<string> {
        if (!this.msalInstance) {
            throw new Error('MSAL instance not initialized');
        }

        // If we have a recent ID token, return it
        if (this.lastAuthResult?.idToken) {
            return this.lastAuthResult.idToken;
        }

        // Otherwise, get a fresh token
        const account = this.getAccount();
        if (!account) {
            throw new Error('No active account');
        }

        try {
            const response = await this.msalInstance.acquireTokenSilent({
                scopes: authConfig.azureAd?.scopes || ['User.Read'],
                account,
            });
            this.lastAuthResult = response;
            return response.idToken || '';
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                // Use redirect instead of popup to avoid nested popup issues
                await this.msalInstance.acquireTokenRedirect({
                    scopes: authConfig.azureAd?.scopes || ['User.Read'],
                    account,
                });
                // This will never be reached as redirect navigates away
                return new Promise(() => {});
            }
            throw error;
        }
    }

    /**
     * Get current account
     */
    getAccount(): AccountInfo | null {
        if (!this.msalInstance) return null;

        const accounts = this.msalInstance.getAllAccounts();
        return accounts.length > 0 ? accounts[0] : null;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated(): boolean {
        return this.getAccount() !== null;
    }

    /**
     * Logout - clears local session without redirecting to Microsoft
     */
    async logout(): Promise<void> {
        if (!this.msalInstance) return;

        // Clear local cache and active account without redirecting
        const account = this.getAccount();
        if (account) {
            this.msalInstance.setActiveAccount(null);
        }

        // Clear the last auth result
        this.lastAuthResult = null;

        // Clear all accounts from cache
        const accounts = this.msalInstance.getAllAccounts();
        accounts.forEach(acc => {
            this.msalInstance?.clearCache();
        });
    }

    /**
     * Get user info
     */
    getUserInfo(): any {
        const account = this.getAccount();
        if (!account) return null;

        return {
            id: account.localAccountId,
            name: account.name || '',
            email: account.username || '',
            roles: [], // Get from token claims if needed
        };
    }
}

export const azureADService = new AzureADService();
